import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_PREFIX = 'odo_search_filters';

function loadPersistedFilters(key: string): Partial<PersistedFilters> {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return {};
        return JSON.parse(raw);
    } catch {
        return {};
    }
}

function persistFilters(key: string, filters: PersistedFilters): void {
    try {
        sessionStorage.setItem(key, JSON.stringify(filters));
    } catch {
        /* storage full or unavailable */
    }
}

interface PersistedFilters {
    searchTerm: string;
    treatmentStatusFilter: string[];
    actionRequiredFilter: string[];
    practiceFilter: string[];
    categoryFilter: string[];
}

type StatFilterType = 'action_required' | 'unread_messages' | 'on_hold' | 'inactive' | null;

interface SearchContextType {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    treatmentStatusFilter: string[];
    setTreatmentStatusFilter: (statuses: string[]) => void;
    actionRequiredFilter: string[];
    setActionRequiredFilter: (actions: string[]) => void;
    practiceFilter: string[];
    setPracticeFilter: (practices: string[]) => void;
    categoryFilter: string[];
    setCategoryFilter: (categories: string[]) => void;
    activeStatFilter: StatFilterType;
    setActiveStatFilter: (filter: StatFilterType) => void;
    clearSearch: () => void;
}

export const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
    children: ReactNode;
    storageKey?: string;
}

export const SearchProvider = ({ children, storageKey }: SearchProviderProps) => {
    const resolvedKey = `${STORAGE_PREFIX}${storageKey ? `:${storageKey}` : ''}`;
    const [persisted] = useState(() => loadPersistedFilters(resolvedKey));
    const [searchTerm, setSearchTermRaw] = useState(persisted.searchTerm ?? '');
    const [treatmentStatusFilter, setTreatmentStatusFilterRaw] = useState<string[]>(
        persisted.treatmentStatusFilter ?? []
    );
    const [actionRequiredFilter, setActionRequiredFilterRaw] = useState<string[]>(
        persisted.actionRequiredFilter ?? []
    );
    const [practiceFilter, setPracticeFilterRaw] = useState<string[]>(
        persisted.practiceFilter ?? []
    );
    const [categoryFilter, setCategoryFilterRaw] = useState<string[]>(
        persisted.categoryFilter ?? []
    );
    const [activeStatFilter, setActiveStatFilter] = useState<StatFilterType>(null);

    const sync = useCallback(
        (patch: Partial<PersistedFilters>) => {
            persistFilters(resolvedKey, {
                searchTerm,
                treatmentStatusFilter,
                actionRequiredFilter,
                practiceFilter,
                categoryFilter,
                ...patch
            });
        },
        [
            resolvedKey,
            searchTerm,
            treatmentStatusFilter,
            actionRequiredFilter,
            practiceFilter,
            categoryFilter
        ]
    );

    const setSearchTerm = useCallback(
        (v: string) => {
            setSearchTermRaw(v);
            sync({ searchTerm: v });
        },
        [sync]
    );
    const setTreatmentStatusFilter = useCallback(
        (v: string[]) => {
            setTreatmentStatusFilterRaw(v);
            sync({ treatmentStatusFilter: v });
        },
        [sync]
    );
    const setActionRequiredFilter = useCallback(
        (v: string[]) => {
            setActionRequiredFilterRaw(v);
            sync({ actionRequiredFilter: v });
        },
        [sync]
    );
    const setPracticeFilter = useCallback(
        (v: string[]) => {
            setPracticeFilterRaw(v);
            sync({ practiceFilter: v });
        },
        [sync]
    );
    const setCategoryFilter = useCallback(
        (v: string[]) => {
            setCategoryFilterRaw(v);
            sync({ categoryFilter: v });
        },
        [sync]
    );

    const clearSearch = useCallback(() => {
        setSearchTermRaw('');
        setTreatmentStatusFilterRaw([]);
        setActionRequiredFilterRaw([]);
        setPracticeFilterRaw([]);
        setCategoryFilterRaw([]);
        setActiveStatFilter(null);
        try {
            sessionStorage.removeItem(resolvedKey);
        } catch {
            /* */
        }
    }, [resolvedKey]);

    const value = useMemo(
        () => ({
            searchTerm,
            setSearchTerm,
            treatmentStatusFilter,
            setTreatmentStatusFilter,
            actionRequiredFilter,
            setActionRequiredFilter,
            practiceFilter,
            setPracticeFilter,
            categoryFilter,
            setCategoryFilter,
            activeStatFilter,
            setActiveStatFilter,
            clearSearch
        }),
        [
            searchTerm,
            treatmentStatusFilter,
            actionRequiredFilter,
            practiceFilter,
            categoryFilter,
            activeStatFilter,
            clearSearch
        ]
    );

    return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export type { StatFilterType };

export function useSearch(): SearchContextType {
    const context = useContext(SearchContext);
    if (context === undefined) {
        throw new Error('useSearch must be used within a SearchProvider');
    }
    return context;
}
